import React from "react"
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material"
import {
    CheckCircleOutline,
    DescriptionOutlined,
    PauseCircleOutline
} from "@mui/icons-material"
import { STATUS, fullName, getStatusKey, getStatusLabel, initials } from "./helpers"

export function StatusBadge({ student }) {
    const key = getStatusKey(student)
    return (
        <span
            className={[
                "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                STATUS[key]?.badge || "bg-slate-100 text-slate-600 ring-slate-200"
            ].join(" ")}
        >
            {getStatusLabel(student)}
        </span>
    )
}

function Avatar({ student }) {
    const photo = student?.meta_data?.photo
    if (typeof photo === "string" && /^(https?:|data:image)/.test(photo)) {
        return <img src={photo} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
    }
    return (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            {initials(student)}
        </span>
    )
}

function Parent({ name, occupation }) {
    return (
        <div className="min-w-0">
            <div className="truncate text-slate-700">{name || "-"}</div>
            {occupation && <div className="truncate text-xs text-slate-400">{occupation}</div>}
        </div>
    )
}

function RowActions({ student, busy, onAdmit, onHold, onViewDocs }) {
    const status = getStatusKey(student)
    const admitted = status === "admit"

    if (busy) {
        return (
            <div className="flex h-8 items-center justify-end pr-2">
                <CircularProgress size={18} />
            </div>
        )
    }

    return (
        <div className="flex items-center justify-end gap-1.5">
            <Button
                size="small"
                variant="contained"
                color="success"
                disableElevation
                startIcon={<CheckCircleOutline fontSize="small" />}
                disabled={admitted}
                onClick={() => onAdmit(student)}
                sx={{ textTransform: "none", borderRadius: "8px", px: 1.5 }}
            >
                {admitted ? "Admitted" : "Admit"}
            </Button>
            <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<PauseCircleOutline fontSize="small" />}
                disabled={admitted || status === "hold"}
                onClick={() => onHold(student)}
                sx={{ textTransform: "none", borderRadius: "8px", px: 1.5 }}
            >
                Hold
            </Button>
            <Tooltip title="View documents" arrow>
                <IconButton
                    size="small"
                    aria-label={`View documents for ${fullName(student)}`}
                    onClick={() => onViewDocs(student)}
                >
                    <DescriptionOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    )
}

/**
 * Registered students for one class: a table on wide screens and stacked
 * cards on phones. Every action receives the student it belongs to.
 */
export default function StudentList({ students, busyId, onAdmit, onHold, onViewDocs }) {
    const actionProps = { onAdmit, onHold, onViewDocs }

    return (
        <>
            {/* Wide screens */}
            <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[860px] text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <th className="w-10 py-3 pl-5 pr-2 font-semibold">#</th>
                            <th className="px-3 py-3 font-semibold">Student</th>
                            <th className="px-3 py-3 font-semibold">Father</th>
                            <th className="px-3 py-3 font-semibold">Mother</th>
                            <th className="px-3 py-3 font-semibold">Gender</th>
                            <th className="px-3 py-3 font-semibold">DOB</th>
                            <th className="px-3 py-3 font-semibold">Contact</th>
                            <th className="px-3 py-3 font-semibold">Status</th>
                            <th className="py-3 pl-3 pr-5 text-right font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {students.map((student, index) => (
                            <tr key={student.id ?? student.email} className="align-middle hover:bg-slate-50/70">
                                <td className="py-3 pl-5 pr-2 tabular-nums text-slate-400">{index + 1}</td>
                                <td className="px-3 py-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <Avatar student={student} />
                                        <div className="min-w-0">
                                            <div className="truncate font-medium text-slate-800">
                                                {fullName(student)}
                                            </div>
                                            <div className="truncate text-xs text-slate-500">
                                                {student.email || "-"}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="max-w-[160px] px-3 py-3">
                                    <Parent name={student.fathername} occupation={student.fatherOccupation} />
                                </td>
                                <td className="max-w-[160px] px-3 py-3">
                                    <Parent name={student.mothername} occupation={student.motherOccupation} />
                                </td>
                                <td className="px-3 py-3 capitalize text-slate-700">{student.gender || "-"}</td>
                                <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">{student.age || "-"}</td>
                                <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">{student.pnumber || "-"}</td>
                                <td className="px-3 py-3"><StatusBadge student={student} /></td>
                                <td className="py-3 pl-3 pr-5">
                                    <RowActions student={student} busy={busyId === student.id} {...actionProps} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Phones */}
            <ul className="divide-y divide-slate-100 md:hidden">
                {students.map((student) => (
                    <li key={student.id ?? student.email} className="space-y-3 px-4 py-4">
                        <div className="flex items-start gap-3">
                            <Avatar student={student} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="truncate font-medium text-slate-800">{fullName(student)}</div>
                                    <StatusBadge student={student} />
                                </div>
                                <div className="truncate text-xs text-slate-500">{student.email || "-"}</div>
                            </div>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                            <div>
                                <dt className="text-slate-400">Father</dt>
                                <dd className="truncate text-slate-700">{student.fathername || "-"}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">Mother</dt>
                                <dd className="truncate text-slate-700">{student.mothername || "-"}</dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">Gender · DOB</dt>
                                <dd className="truncate capitalize text-slate-700">
                                    {student.gender || "-"} · {student.age || "-"}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-slate-400">Contact</dt>
                                <dd className="truncate tabular-nums text-slate-700">{student.pnumber || "-"}</dd>
                            </div>
                        </dl>
                        <RowActions student={student} busy={busyId === student.id} {...actionProps} />
                    </li>
                ))}
            </ul>
        </>
    )
}
